/**
 * FilePropertiesDialog — info modal for a single file.
 *
 * Shows path, size, last-modified, content type. Read-only — no actions
 * here, the kebab menu is the action surface.
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { humanReadableSize } from '@utils/format/fileSize';

export interface FilePropertiesDialogProps {
  open: boolean;
  /** Full path within the bucket. */
  path: string;
  size?: number;
  lastModified?: string | null;
  contentType?: string;
  onClose: () => void;
}

function inferContentType(path: string): string {
  const ext = (path.split('.').pop() || '').toLowerCase();
  const map: Record<string, string> = {
    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
    webp: 'image/webp', avif: 'image/avif', svg: 'image/svg+xml',
    pdf: 'application/pdf', txt: 'text/plain', md: 'text/markdown',
    json: 'application/json', mp4: 'video/mp4', mov: 'video/quicktime',
    mp3: 'audio/mpeg', wav: 'audio/wav', zip: 'application/zip',
  };
  return map[ext] || (ext ? `application/${ext}` : 'application/octet-stream');
}

export function FilePropertiesDialog({
  open, path, size, lastModified, contentType, onClose,
}: FilePropertiesDialogProps) {
  const ct = contentType || inferContentType(path);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Properties</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Row label="Path" value={`/${path}`} mono />
          <Row label="Size" value={size !== undefined ? humanReadableSize(size) : '—'} />
          <Row
            label="Modified"
            value={lastModified ? new Date(lastModified).toLocaleString() : '—'}
          />
          <Row label="Type" value={ct} mono />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography sx={{ minWidth: 80, fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>
        {value}
      </Typography>
    </Stack>
  );
}
